import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import { locales } from '../../../i18n';
import { 
  Box, 
  TextField, 
  InputAdornment, 
  Select, 
  MenuItem, 
  Chip   
} from '@mui/material';

import Icon from '../../../assets/TouristIcon.png';
import useWindowDimensions from '../../../hooks/useWindowDimensions';
import useGetCountries from '../../../hooks/useGetCountries';
import { Text18 } from '../../../ui/Text18';
import { 
  InsuranceParameters, 
  IInsurance,
  SetInsuranceParameters, 
  SetInsurance,
  DefaultTouristData,   
  UserTouristErrors,
  CountryType 
} from '../../../types';
import { 
  LATIN_REGEX, 
  NUMBER_REGEX,
  PASSPORT_REGEX,
  MAIL_REGEX  
} from '../../../constants/steps';
import { 
  TouristWrapper,
  Header,
  TouristIcon,
  TouristTitle,
  Inputs,
  AllInputsWrapper,
  SelectWrapper,
  SelectBlock,
  Label
} from '../styles';

type Props = {
  index: number;  
  insurance: IInsurance;
  parameters: InsuranceParameters;
  setInsurance: SetInsurance;
  setInsuranceParameters: SetInsuranceParameters; 
};

const TouristData: React.FC<Props> = ({
  index, 
  parameters,
  insurance,
  setInsuranceParameters,
  setInsurance  
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation(); 

  const { selectedTourists } = parameters || {}; 
  const { name, surname, idnp, passportNumber, phoneNumber, email, country, city } =
    selectedTourists[index] || {}; 

  const defaultTouristData: DefaultTouristData = {
    name,
    surname, 
    passportNumber,  
    idnp, 
    phoneNumber,
    country,
    city,
  };

  const defaultTouristErrors: UserTouristErrors = {
    name: false,
    surname: false, 
    passportNumber: false, 
    idnp: false, 
    phoneNumber: false,   
    city: false,
  };  

  if (index === 0) {
    defaultTouristData.email = email;
    defaultTouristErrors.email = false;
  };
  
  const [touristData, setTouristData] = useState(defaultTouristData);
  const [userTouristErrors, setTouristDataErrors] = useState(defaultTouristErrors);
  const { countries } = useGetCountries();
  const availableCountries = countries?.popular.concat(countries?.other);
  const { width } = useWindowDimensions();
  
  const beneficiaries = [...insurance.beneficiary];  

  const fields = [
    touristData.name !== '',
    touristData.surname !== '',
    touristData.passportNumber !== '',
    PASSPORT_REGEX.test(touristData.passportNumber),
    touristData.idnp !== '',
    touristData.idnp.length === 13,
    touristData.phoneNumber !=='',
    touristData.phoneNumber.length === 8,    
    touristData.city !== '',
  ];

  if (index === 0) {
    fields.push(touristData.email !== '', MAIL_REGEX.test(touristData?.email));
  };

  const changeBeneficiar = (field: string, value: string) => {
    switch(field) {
      case 'name': {
        beneficiaries[index].first_name = value;       
        setInsurance({
          ...insurance,
          beneficiary: beneficiaries,
          payer: {
            ...insurance.payer,
            first_name: `${index === 0 ? value : ''}`
          }
        })
        break;
      }
      case 'surname': {
        beneficiaries[index].last_name = value;       
        setInsurance({
          ...insurance,
          beneficiary: beneficiaries,
          payer: {
            ...insurance.payer,
            last_name: `${index === 0 ? value : ''}`
          }
        })
        break;
      }
      case 'passportNumber': {
        beneficiaries[index].passport_series_number = value;       
        setInsurance({
          ...insurance,
          beneficiary: beneficiaries,          
        })
        break;
      }
      case 'country': {                
        beneficiaries[index].country = value;       
        setInsurance({
          ...insurance,
          beneficiary: beneficiaries,         
        });        
        break;
      }
      case 'city': {
        beneficiaries[index].city = value;       
        setInsurance({
          ...insurance,
          beneficiary: beneficiaries,         
        })
        break;
      }
      case 'idnp': {
        beneficiaries[index].idnp = value;       
        setInsurance({
          ...insurance,
          beneficiary: beneficiaries,         
        })
        break;
      }
      case 'phoneNumber': {
        beneficiaries[index].phone = value;       
        setInsurance({
          ...insurance,
          beneficiary: beneficiaries,
          payer: {
            ...insurance.payer,
            phone: `${index === 0 ? value : ''}`
          }
        })
        break;
      }
      case 'email': {
        beneficiaries[index].email = value;       
        setInsurance({
          ...insurance,
          beneficiary: beneficiaries,
          payer: {
            ...insurance.payer,
            email: `${index === 0 ? value : ''}`
          }
        })
        break;
      }
      default:
    }
  }

  const handleAddData = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    if(field !== 'country')  event.persist();
    let newData = selectedTourists;
    const newValue = event.target.value;    
    const errorExist = !newValue || 
      (field === 'idnp' && newValue.length < 13) || 
      (field === 'phoneNumber' && newValue.length < 8) ||  
      (field === 'passportNumber' && !PASSPORT_REGEX.test(newValue)) ||    
      (field === 'email' && !MAIL_REGEX.test(newValue));    
    
    const type = (field !== 'country') ? event.target.id : field;       

    if (field !== 'country') setTouristDataErrors(prev => ({
      ...prev,
      [field]: errorExist,
    }));  

    if (field === 'idnp' && newValue.length > 13) return;
    if (field === 'phoneNumber' && newValue.length > 8) return;

    setTouristData(prevState => ({
      ...prevState,
      [field]: event.target.value,
    }));   
    
    newData[index] = { ...selectedTourists[index], [type]: newValue };   
    
    if (beneficiaries[index]) changeBeneficiar(field, newValue);    
    
    setInsuranceParameters({
      ...parameters,
      selectedTourists: newData,
    });        
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const { key } = event;
    const target = event.target as HTMLInputElement;  

    switch (target.id) {
      case 'phoneNumber': {
        if (!NUMBER_REGEX.test(key)) {
          event.preventDefault();
        }
        break;
      }   
      case 'idnp': {
        if (!NUMBER_REGEX.test(key)) {
          event.preventDefault();
        }
        break;
      }     
      case 'surname': {
        if (!LATIN_REGEX.test(key)) {
          event.preventDefault();
        }
        break;
      }
      case 'name': {
        if (!LATIN_REGEX.test(key)) {
          event.preventDefault();
        }
        break;
      }     
      case 'city': {
        if (!LATIN_REGEX.test(key)) {
          event.preventDefault();
        }
        break;
      }
      default:
    }
  }; 

  useEffect(() => {   
    const countryIsSelected = !!selectedTourists[index].country;
    const fieldStatus = fields.every(field => field === true);  
    const fieldsCompleted = !!fieldStatus && countryIsSelected;    
    let dataTouristWithCountry = selectedTourists;
    dataTouristWithCountry[index] = { ...selectedTourists[index], fieldsCompleted: fieldsCompleted };
    setInsuranceParameters({
      ...parameters,
      selectedTourists: dataTouristWithCountry,
    });      
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [touristData])

  const touristBirthDate: any = selectedTourists[index]?.birthDate; 

  const handleAddBirthDate = (birthDate: any) => {
    const allTourists = [...selectedTourists];
    allTourists[index].birthDate = birthDate;  
    beneficiaries[index].date_of_birth = new Date(birthDate).toISOString();
    
    setInsuranceParameters({
      ...parameters,
      selectedTourists: allTourists,
    });

    setInsurance({
      ...insurance,
      beneficiary: beneficiaries
    });
  };  
  
  const handleDelete = (event: any) => {   
    const countryValue = 'country';

    setTouristData(prevState => ({
      ...prevState,
      [countryValue]: '',
    }));  

    changeBeneficiar(countryValue, '');

    selectedTourists[index][countryValue] = '';

    setInsuranceParameters({
      ...parameters,
      selectedTourists,
    });
  };

  return (
    <TouristWrapper>
      <Header>
        <TouristIcon src={Icon} />
        <TouristTitle>{`${t('tourist')} ${index + 1}`}</TouristTitle>
      </Header>
      <Inputs>
        <Box
          component='form'
          sx={{
            '& .MuiTextField-root': {
              m: 1,
              width: width > 1035 ? '33%' : '100%',
              minWidth: width > 1035 ? '33%' : '100%',
            },           
          }}
        >
          <AllInputsWrapper>
            <TextField
              id='name'
              label={t('name')}
              variant='outlined'
              value={name || ''}
              onChange={handleAddData('name')}
              onKeyPress={handleKeyPress}
              error={userTouristErrors.name}
              helperText={t('enter_your_name')}
            />
            <TextField
              id='surname'
              label={t('surname')}
              variant='outlined'
              value={surname || ''}
              onChange={handleAddData('surname')}
              onKeyPress={handleKeyPress}
              error={userTouristErrors.surname}
              helperText={t('enter_your_surname')}
            />
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={locales[language]}
            >
              <DatePicker
                label={t('birth_date')}
                disableFuture
                disableMaskedInput
                value={touristBirthDate}
                onChange={handleAddBirthDate}
                renderInput={(params) => <TextField {...params} />}
              />
            </LocalizationProvider>
          </AllInputsWrapper>
          <AllInputsWrapper
            style={{
              marginTop: 24,
            }}
          >
            <TextField
              id='passportNumber'
              label={t('passportNumber')}
              variant='outlined'
              value={passportNumber || ''}
              onChange={handleAddData('passportNumber')}             
              error={userTouristErrors.passportNumber}
              helperText={t('enter_passportNumber')}
            />
            <TextField
              id='idnp'
              label={t('idnp')}
              value={idnp || ''}
              variant='outlined'
              onChange={handleAddData('idnp')}
              onKeyPress={handleKeyPress}
              error={userTouristErrors.idnp}
              helperText={t('enter_idnp')}
            />
            <TextField
              id='phoneNumber'
              label={`${t('phoneNumber')}*`}
              variant='outlined'
              value={phoneNumber || ''}
              onChange={handleAddData('phoneNumber')}
              onKeyPress={handleKeyPress}
              error={userTouristErrors.phoneNumber}             
              helperText={t('enter_phoneNumber')}
              InputProps={{
                startAdornment: <InputAdornment position="start">+373</InputAdornment>,
              }}
            />            
          </AllInputsWrapper>
          <AllInputsWrapper
            style={{
              marginTop: 24,
            }}            
            >
            {
              index === 0 && (
              <TextField
                id='email'
                label='Email*'
                variant='outlined'
                value={email || ''}
                onChange={handleAddData('email')}                 
                error={userTouristErrors.email}                 
              />
              )
            }            
            <SelectBlock>
              <SelectWrapper>
                <Label id='country'>{t('country')}</Label>
                <Select        
                  sx={{
                    height: '56px'
                  }}                                 
                  labelId='country'
                  label={t('country')}
                  value={ country || '' }               
                  onChange={handleAddData('country')}
                  renderValue={(selected: string) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      <Chip
                        key={selected}
                        label={selected}
                        onMouseDown={e => e.stopPropagation()}
                        onDelete={handleDelete}
                      />             
                    </Box>
                  )}
                >
                  {availableCountries?.map((country: CountryType, index: number) => {
                    return (
                      <MenuItem
                        key={country.ccn3 + index}
                        id={country.ccn3}
                        value={
                          country.language[language.slice(0, 2)]
                        }
                      >
                        <Text18>{country.language[language.slice(0, 2)]}</Text18>
                      </MenuItem>
                    );
                  })}
                </Select>
              </SelectWrapper>
            </SelectBlock>
            <TextField
              id='city'
              label={t('city')}
              value={city || ''}
              variant='outlined'
              onChange={handleAddData('city')}
              onKeyPress={handleKeyPress}
              error={userTouristErrors.city}             
            />
          </AllInputsWrapper>
        </Box>
      </Inputs>
    </TouristWrapper>
  );
};

export default TouristData;
